import React, { useCallback, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Refresh, Settings } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { fetchMetricsHistory } from "@/lib/api";
import {ApexChart, rotateColors} from "@/components/ApexChart";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {Switch} from "@/components/ui/switch";
import {Label} from "@/components/ui/label";

const DURATIONS: { label: string; minutes: number }[] = [
  { label: "5m", minutes: 5 },
  { label: "15m", minutes: 15 },
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "6h", minutes: 360 },
  { label: "12h", minutes: 720 },
  { label: "24h", minutes: 1440 },
  { label: "3d", minutes: 4320 },
  { label: "7d", minutes: 10080 },
  { label: "30d", minutes: 43200 },
  { label: "90d", minutes: 129600 },
  { label: "365d", minutes: 525600 },
];

const CHART_PANELS: { title: string; keys: string[]; group?: string; desc?: string }[] = [
  // Core
  { title: "Messages / sec", keys: ["messages_per_second"], group: "Core" },
  { title: "Active Units", keys: ["active_units"], group: "Core" },
  { title: "Charging", keys: ["charging_units"], group: "Core" },
  { title: "Total Power (kW)", keys: ["total_power_kw"], group: "Core" },
  { title: "Queue Depth", keys: ["queue_depth"], group: "Core", desc: "Main telemetry message queue (50k capacity). Messages wait here before being picked up by workers." },
  { title: "Unsupported Units", keys: ["unsupported_units"], group: "Core" },
  // Queue processing
  { title: "WUP Queue Depth", keys: ["wup_queue_depth"], group: "Queues", desc: "Wake-Up queue (1k capacity, 2 workers). Carries immediate command updates triggered by dashboard changes, LB recalc, or gate verdicts." },
  { title: "UDP Gate Self-Heal Queue", keys: ["gate_eval_queue_peak", "gate_eval_queue_depth"], group: "Queues", desc: "Local queue of the UDP listener's gate self-heal (10k capacity, 4 workers): re-checks for sessions seen with no gate verdict. In the split deployment the UDP listener does not own the orchestrator/PDP, so these runs end as no_orchestrator — a no-op, not a completed authorization. This chart does NOT show the Core-side (PDP) authorization backlog. Peak = deepest point within each sample interval; depth = point sample at the sample time." },
  { title: "UDP Gate Self-Heal Outcomes (/sec)", keys: ["gate_eval_evaluated_per_second", "gate_eval_no_orchestrator_per_second", "gate_eval_timed_out_per_second", "gate_eval_failed_per_second"], group: "Queues", desc: "How the UDP listener's local self-heal runs ended. No orchestrator = this process has no PDP (split deployment), so the run was a no-op — not an authorization; Core keeps unit gates current itself. Evaluated = evaluate_permit() was called in this process. Timed out = evaluate_permit() took over 5s. Core-side authorization is not counted here." },
  { title: "Session Effect Queue", keys: ["effect_queue_depth"], group: "Queues", desc: "Session effect processor queue (4 sharded workers). Handles DB saves, event publishing, and billing callbacks for session state changes." },
  // Correlation / delay
  { title: "Recv vs Proc Rate", keys: ["received_per_second", "messages_per_second"], group: "Correlation", desc: "Gap between UDP receive rate and processing rate indicates backpressure building in the main queue." },
  { title: "Session Rate (/sec)", keys: ["sessions_per_second", "session_events_per_second"], group: "Correlation", desc: "Sessions started per second and total session events (starts + ends) per second. Indicates charging throughput." },
  { title: "Effect Processing Rate", keys: ["effects_per_second"], group: "Correlation", desc: "Session effects (start/end/save) processed per second. Drop in rate + rising effect queue = DB or orchestrator bottleneck." },
  { title: "Session Save Latency (ms)", keys: ["session_save_avg_ms", "session_save_p95_ms"], group: "Correlation", desc: "Latency of each session-row write by the session effect workers (new start row, end, progress checkpoint, save/subsession split), unit-fence wait included; calls that raised or wrote no row are not counted. Avg and p95 over each sample interval; a gap means no writes in that interval. If Session Latency Samples Missed is above 0, avg/p95 for that interval cover only the samples still held. Spikes here back up the session effect workers." },
  { title: "Session Latency Samples Missed", keys: ["session_save_latency_samples_missed"], group: "Correlation", desc: "Session-row writes whose latency sample was pushed out of the bounded latency log (4096 samples) before the interval was read. Normally 0. Above 0, Session Save Latency for that interval is computed from a partial window (the most recent samples only); the writes still count in Disk I/O Rate." },
  { title: "UDP Worker Stalls (/sec)", keys: ["worker_stalls_per_second"], group: "Correlation", desc: "Stalls in the 32 UDP telemetry worker threads. A stall = single message processing took >60ms (parse, update state, send response). Common causes: geo IP lookup, lock contention, slow socket send." },
  { title: "Drop Rate (/sec)", keys: ["messages_dropped_rate", "gate_eval_dropped_rate", "wup_dropped_rate"], group: "Correlation", desc: "Messages dropped per second across all queues when they hit capacity. Any non-zero value means the system is saturated." },
  { title: "UDP Gate Self-Heal Evaluation Latency (ms)", keys: ["gate_eval_avg_ms"], group: "Correlation", desc: "Average evaluate_permit() call time of the UDP listener's local self-heal runs (rolling 200 samples). Only runs that reached an orchestrator in this process count; no_orchestrator no-ops are excluded, so in the split deployment (UDP does not own the PDP) the chart stays empty. Does not show Core-side authorization latency. Can reach 500ms+ or time out at 5s." },
  // Disk I/O
  { title: "Disk I/O Rate (/sec)", keys: ["log_writes_per_second", "db_writes_per_second"], group: "Disk I/O", desc: "CSV message log writes and session-row writes to PostgreSQL by the session effect workers per second. Spikes correlate with session save latency and effect queue backup." },
  { title: "Log Writer Queue", keys: ["log_queue_depth"], group: "Disk I/O", desc: "Message logger async write queue. If this grows, the CSV writer thread can't keep up with incoming log entries." },
  // Telemetry archive (Rust stream backend)
  { title: "Archiver Lag / Pending", keys: ["archiver_lag", "archiver_pending"], group: "Telemetry Archive", desc: "Telemetry stream consumer-group health (XINFO GROUPS). Lag = entries XADD'd but not yet read by the archiver (the 'falling behind' signal — should hover near 0). Pending = read but not yet acked (archiver consuming but stuck writing). Rising lag with no archiver heartbeat = archiver down." },
  { title: "Archiver Writes (/sec)", keys: ["archiver_writes_per_second"], group: "Telemetry Archive", desc: "Entries durably archived per second, from the Rust archiver's heartbeat. 0 while traffic flows = archiver down or not keeping up. Only populated when TELEMETRY_ARCHIVE_BACKEND is 'redis'/'both'." },
  { title: "Archive Stream Length", keys: ["archiver_stream_length"], group: "Telemetry Archive", desc: "Total entries in the telemetry:raw stream (XLEN), trimmed to ~TELEMETRY_STREAM_MAXLEN. Context for lag — a stream pinned at MAXLEN with high lag means the archiver can't drain fast enough and entries are being trimmed before archival." },
];

const METRIC_NAMES = CHART_PANELS.flatMap((p) => p.keys);

/** Human-readable series labels for metrics whose auto-generated name (underscores → spaces) is unclear. */
const SERIES_LABELS: Record<string, string> = {
  log_writes_per_second: "CSV log appends",
  db_writes_per_second: "Session-row writes",
  gate_eval_queue_peak: "Peak in interval",
  session_save_latency_samples_missed: "Samples missed",
  gate_eval_queue_depth: "At sample time",
  gate_eval_evaluated_per_second: "Evaluated",
  gate_eval_no_orchestrator_per_second: "No orchestrator (no-op)",
  gate_eval_timed_out_per_second: "Timed out",
  gate_eval_failed_per_second: "Failed",
  sessions_per_second: "Sessions started",
  session_events_per_second: "Session events (start+end)",
  archiver_lag: "Lag (undelivered)",
  archiver_pending: "Pending (unacked)",
  archiver_writes_per_second: "Archived/sec",
};

const MAX_CHART_POINTS = 720;

const VISIBILITY_KEY = "evse-metrics-chart-visibility";

function loadHiddenCharts(): Set<string> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch { /* ignore */ }
  return new Set();
}

function saveHiddenCharts(hidden: Set<string>) {
  localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...hidden]));
}

/**
 * Y-axis scaling mode, applied live to every chart on the page:
 *  - "auto":   ApexCharts fits the full data min/max (default behavior).
 *  - "zero":   axis floored at 0, top auto-fit — magnitude shown true-to-zero.
 *  - "robust": axis fits the central ~95% (p2.5–p97.5) so rare outliers clip
 *              instead of flattening the visible range. Tooltip still shows the
 *              real value, so clipped spikes are one hover away.
 */
type YScale = "auto" | "zero" | "robust";

const YSCALE_KEY = "evse-metrics-yscale";
const YSCALE_OPTIONS: { value: YScale; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "zero", label: "From 0" },
  { value: "robust", label: "Fit 95%" },
];

function loadYScale(): YScale {
  const stored = localStorage.getItem(YSCALE_KEY);
  return stored === "zero" || stored === "robust" ? stored : "auto";
}

/**
 * Compute fixed y-axis bounds for one panel's pooled values under the given
 * mode. Returns {} for "auto" (let ApexCharts decide) or when there's no data.
 * Cheap: a single sort of <= MAX_CHART_POINTS numbers, run once per data refresh.
 */
function computeBounds(values: number[], mode: YScale): { yMin?: number; yMax?: number } {
  if (mode === "auto" || values.length === 0) return {};
  if (mode === "zero") return { yMin: 0 };
  // robust: clip to the central band, then pad ~8% so the line isn't glued to the edge.
  const sorted = [...values].sort((a, b) => a - b);
  const at = (qt: number) => sorted[Math.min(sorted.length - 1, Math.floor(qt * (sorted.length - 1)))];
  const lo = at(0.025);
  const hi = at(0.975);
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.08 || 1;
  return { yMin: lo - pad, yMax: hi + pad };
}

export function MetricsPage() {
  const [duration, setDuration] = useState(60);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenCharts, setHiddenCharts] = useState<Set<string>>(loadHiddenCharts);
  const [yScale, setYScale] = useState<YScale>(loadYScale);

  const toggleChart = useCallback((title: string) => {
    setHiddenCharts((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      saveHiddenCharts(next);
      return next;
    });
  }, []);

  const q = useQuery({
    queryKey: ["metrics", "history", duration],
    queryFn: () => fetchMetricsHistory(duration),
    refetchInterval: autoRefresh ? 10_000 : false,
    placeholderData: keepPreviousData,
  });

  const metricsRaw = q.data?.metrics ?? {};
  const stats = q.data?.stats ?? {};
  const isStale = !!q.data?.stale;
  const staleReason = isStale ? q.data?.reason : null;

  // Decimate to <= MAX_CHART_POINTS, tracking the REAL source index of each kept
  // sample so value lookups stay aligned. (Previously the always-appended final
  // point was read at decimatedIdx*step, which overshot the array and fell back
  // to 0 — fabricating a trailing zero that dragged the y-axis min down to 0.)
  const { timestamps, indices } = useMemo(() => {
    const rawTs: number[] = (metricsRaw.timestamps ?? []).map((ts: string) => new Date(ts).getTime());
    const s = rawTs.length > MAX_CHART_POINTS ? Math.ceil(rawTs.length / MAX_CHART_POINTS) : 1;
    const idx: number[] = [];
    for (let i = 0; i < rawTs.length; i += s) idx.push(i);
    // Always include the most recent sample, even if it isn't on the stride.
    if (idx.length && idx[idx.length - 1] !== rawTs.length - 1) idx.push(rawTs.length - 1);
    return { timestamps: idx.map((i) => rawTs[i]), indices: idx };
  }, [metricsRaw.timestamps]);

  const hasData = timestamps.length > 0;

  const activePanels = useMemo(
    () =>
      CHART_PANELS.filter(
        (panel) =>
          !hiddenCharts.has(panel.title) &&
          panel.keys.some(
            (key) => Array.isArray(metricsRaw[key]) && metricsRaw[key].length > 0,
          ),
      ),
    [metricsRaw, hiddenCharts],
  );

  const panelSeries = useMemo(
    () =>
      activePanels.map((panel) =>
        panel.keys.map((key) => {
          const arr = metricsRaw[key] as (number | null)[] | undefined;
          return {
            name: SERIES_LABELS[key] ?? key.replace(/_/g, " "),
            // A missing value is a hole in the line, never a measured 0.
            data: indices.map((srcIdx, k) => [timestamps[k], arr?.[srcIdx] ?? null] as [number, number | null]),
          };
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePanels, timestamps, indices, ...METRIC_NAMES.map((k) => metricsRaw[k])],
  );

  // Fixed y-axis bounds per panel for the active scaling mode. Pools all of a
  // panel's series so multi-series charts share one comparable scale. Recomputed
  // only when the series or mode change (i.e. once per ~10s refresh), never on hover.
  const panelBounds = useMemo(
    () =>
      panelSeries.map((seriesList) => {
        const values = seriesList.flatMap((s) =>
          s.data.flatMap(([, v]) => (v === null ? [] : [v])));
        return computeBounds(values, yScale);
      }),
    [panelSeries, yScale],
  );

  const flatStats = useMemo(() => {
    const result: { key: string; value: string }[] = [];
    for (const [k, v] of Object.entries(stats)) {
      if (typeof v === "number") {
        result.push({ key: k, value: Number.isInteger(v) ? String(v) : (v as number).toFixed(2) });
      } else if (typeof v === "string") {
        const d = new Date(v);
        if (!isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(v)) {
          result.push({ key: k, value: d.toLocaleString([], { dateStyle: "short", timeStyle: "medium" }) });
        } else {
          result.push({ key: k, value: v });
        }
      }
    }
    return result;
  }, [stats]);

  const groups = useMemo(() => {
    const seen = new Set<string>();
    return CHART_PANELS.reduce<string[]>((acc, p) => {
      const g = p.group ?? "Other";
      if (!seen.has(g)) { seen.add(g); acc.push(g); }
      return acc;
    }, []);
  }, []);

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1>Metrics</h1>
          <div className="text-sm opacity-70">Time-series metrics and system performance</div>
        </div>
        <div className="flex items-center gap-2">
          <Label>Auto Refresh:</Label>
          <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} size="sm" />
          <Button variant={"outline"} size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
          <Button variant={showSettings ? "default" : "outline"} size="sm" onClick={() => setShowSettings((v) => !v)}>
            <Settings className="h-4 w-4" /> Charts
          </Button>

          <Label>Y-axis:</Label>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={yScale}
            onValueChange={(v) => {
              if (!v) return;
              setYScale(v as YScale);
              localStorage.setItem(YSCALE_KEY, v);
            }}
          >
            {YSCALE_OPTIONS.map((o) => (
              <ToggleGroupItem key={o.value} value={o.value}>
                {o.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <ToggleGroup type="single" size="sm" variant="outline" value={String(duration)} onValueChange={(v) => v && setDuration(Number(v))}>
            {DURATIONS.map((d) => (
                <ToggleGroupItem key={d.minutes} value={String(d.minutes)}>
                  {d.label}
                </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      {isStale && (
        <div className="rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning-content">
          Diagnostics unavailable — UDP listener may be down or recently restarted
          {staleReason ? ` (${staleReason})` : ""}.
        </div>
      )}

      {/* Chart visibility settings */}
      {showSettings && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <CardTitle className="text-sm">Chart Visibility</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="text-xs h-6"
                  onClick={() => { setHiddenCharts(new Set()); saveHiddenCharts(new Set()); }}>
                  Show All
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-6"
                  onClick={() => { const all = new Set(CHART_PANELS.map((p) => p.title)); setHiddenCharts(all); saveHiddenCharts(all); }}>
                  Hide All
                </Button>
              </div>
            </div>
            {groups.map((group) => (
              <div key={group} className="mb-2">
                <div className="text-xs font-medium opacity-50 uppercase mb-1">{group}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
                  {CHART_PANELS.filter((p) => (p.group ?? "Other") === group).map((panel) => (
                    <label key={panel.title} className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        size="sm"
                        checked={!hiddenCharts.has(panel.title)}
                        onCheckedChange={() => toggleChart(panel.title)}
                      />
                      <span className="text-xs">{panel.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}


        {/* Stats summary */}
        {flatStats.length > 0 && (
            <Card>
              <CardContent>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {metricsRaw.tier && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs uppercase opacity-50">Data tier:</span>
                      <span className="uppercase">{metricsRaw.tier}</span>
                    </div>
                  )}
                  {flatStats.map((s) => (
                      <div key={s.key} className="flex items-baseline gap-1.5">
                        <span className="text-xs uppercase opacity-50">{s.key.replace(/_/g, " ")}:</span>
                        <span>{s.value}</span>
                      </div>
                  ))}
                </div>
              </CardContent>
            </Card>
        )}



      {/* Charts */}
      {q.isLoading ? (
        <div className="flex justify-center p-12">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : q.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {(q.error as any)?.message?.includes("503")
            ? "Metrics collector is not available"
            : "Failed to load metrics"}
        </div>
      ) : !hasData ? (
        <Card>
          <CardContent className="text-center opacity-60">No metric data available for this time range</CardContent>
        </Card>
      ) : (
        <div className={`grid gap-4 md:grid-cols-2 transition-opacity duration-300 ${q.isFetching ? "opacity-60" : "opacity-100"}`}>
          {activePanels.map((panel, idx) => (
            <Card key={panel.title}>
              <CardContent>
                <CardTitle className="text-sm">{panel.title}</CardTitle>
                {panel.desc && <div className="text-xs opacity-50 mt-0.5 mb-1">{panel.desc}</div>}
                <ApexChart
                  type="area"
                  series={panelSeries[idx]}
                  colors={rotateColors(idx*2)}
                  yMin={panelBounds[idx]?.yMin}
                  yMax={panelBounds[idx]?.yMax}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="text-center text-xs opacity-50">
        {timestamps.length} data points • Window: {DURATIONS.find((d) => d.minutes === duration)?.label || `${duration}m`}
        {autoRefresh && " • Auto-refreshing every 10s"}
      </div>
    </div>
  );
}
